import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { BookOpen, ShoppingCart, CheckCircle, Package, Search, Info, Download, CreditCard, Banknote, ExternalLink, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/libritos';

// Modal personalizado para iframes - se posiciona en el viewport visible
const IframeModal = ({ open, onClose, children, title }) => {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const [modalPosition, setModalPosition] = useState({ top: 0 });
  
  useEffect(() => {
    if (open) {
      // Calcular la posición visible actual (importante para iframes)
      // Usamos la posición de scroll del padre si estamos en iframe
      let scrollTop = window.scrollY || window.pageYOffset || 0;
      
      // Intentar obtener scroll del iframe padre
      try {
        if (window.parent !== window) {
          // Estamos en un iframe, calcular posición relativa
          const rect = document.documentElement.getBoundingClientRect();
          scrollTop = Math.abs(rect.top);
        }
      } catch (e) {
        // Si no podemos acceder al padre por CORS, usar scroll actual
      }
      
      setModalPosition({ top: scrollTop + 20 }); // +20px de margen superior
      
      // Bloquear scroll del body
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div 
      ref={overlayRef}
      className="absolute inset-0 z-50"
      style={{ 
        backgroundColor: 'rgba(0,0,0,0.6)',
        minHeight: '100%'
      }}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {/* Modal posicionado en el viewport visible */}
      <div 
        className="flex justify-center px-4"
        style={{ 
          paddingTop: `${modalPosition.top}px`,
          paddingBottom: '2rem'
        }}
      >
        <div 
          ref={modalRef}
          className="bg-white rounded-xl shadow-2xl w-full max-w-md relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-white border-b px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-xl">
            <h2 className="text-lg sm:text-xl font-semibold text-violet-900 pr-2">{title}</h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          {/* Contenido */}
          <div className="px-4 sm:px-6 py-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// Información de pago y retiro
const PAYMENT_INFO = {
  title: "Cómo retiro y pago",
  content: `Tu libro queda reservado por 72 hs. Para retirarlo, acercate a la sede distrital.
Podés pagar en efectivo en la oficina o anticipadamente por transferencia.

**Datos para transferencia**
Cuenta: Cuenta corriente
N.º de cuenta: 020782/1
CBU: 0140023601506802078210
Alias: cats.lomasdezamora
CUIT: 33-64492045-9 / 30-68816043-6
Entidad: Colegio de Asistentes y Trabajadores Sociales

**¿Cómo confirmo mi pago por transferencia?**
Después de hacer la transferencia, ingresá a "Mis Reservas" y subí el comprobante. Vamos a verificarlo y te avisamos cuando esté listo para retirar.`
};

/**
 * Catálogo de Publicaciones - Versión para Iframe
 * Sin scroll interno - El contenido se expande y el iframe se ajusta manualmente
 */
export default function LibritosCatalogo() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all"); // "all", "sale", "free"
  const [selectedBook, setSelectedBook] = useState(null);
  const [reservationModal, setReservationModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [paymentInfoModal, setPaymentInfoModal] = useState(false);
  const [reservationData, setReservationData] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    payment_method: "efectivo",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBooks();
    
    // Quitar scroll del body/html para embeber en iframe sin scroll interno
    document.documentElement.style.overflow = 'visible';
    document.documentElement.style.height = 'auto';
    document.body.style.overflow = 'visible';
    document.body.style.height = 'auto';
    
    // Variable para guardar última altura enviada y evitar loops
    let lastSentHeight = 0;
    
    // Función para comunicar altura al iframe padre
    const sendHeightToParent = () => {
      const height = document.body.scrollHeight;
      // Solo enviar si la altura cambió más de 10px (evita loops)
      if (Math.abs(height - lastSentHeight) > 10) {
        lastSentHeight = height;
        if (window.parent !== window) {
          window.parent.postMessage({ type: 'libritos-resize', height: height }, '*');
        }
      }
    };
    
    // Enviar altura inicial después de que cargue el contenido
    const initialTimeout = setTimeout(sendHeightToParent, 500);
    
    // NO usar ResizeObserver - causa loops infinitos con iframes
    // Solo enviar en eventos específicos
    
    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.body.style.overflow = '';
      document.body.style.height = '';
      clearTimeout(initialTimeout);
    };
  }, []);

  // Función para notificar altura al padre (usada cuando cambia el contenido)
  const notifyParentHeight = () => {
    if (window.parent !== window) {
      setTimeout(() => {
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: 'libritos-resize', height: height }, '*');
      }, 300);
    }
  };

  // Notificar cuando cambian los libros (después de cargar o filtrar)
  useEffect(() => {
    if (books.length > 0) {
      notifyParentHeight();
    }
  }, [books.length, searchTerm, categoryFilter]);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/books/available`);
      setBooks(response.data);
    } catch (error) {
      console.error("Error al cargar el catálogo:", error);
      toast.error("Error al cargar el catálogo");
    } finally {
      setLoading(false);
    }
  };

  const filteredBooks = books.filter(
    (book) =>
      book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (book.description && book.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Aplicar filtro de categoría
  const categoryFilteredBooks = filteredBooks.filter(book => {
    if (categoryFilter === "all") return true;
    if (categoryFilter === "sale") return !book.is_free;
    if (categoryFilter === "free") return book.is_free;
    return true;
  });

  // Separar libros de pago y gratuitos (para mostrar en secciones)
  const booksForSale = categoryFilteredBooks.filter(book => !book.is_free);
  const freeBooks = categoryFilteredBooks.filter(book => book.is_free);

  const openReservationModal = (book) => {
    setSelectedBook(book);
    setFormData({ customer_name: "", customer_email: "", customer_phone: "", payment_method: "efectivo" });
    setReservationModal(true);
  };

  const handleReservation = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/reservations`, {
        book_id: selectedBook.id,
        ...formData,
      });
      setReservationData(response.data);
      setReservationModal(false);
      setSuccessModal(true);
      fetchBooks();
      toast.success("¡Reserva creada exitosamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear la reserva");
    } finally {
      setSubmitting(false);
    }
  };

  const getStockBadge = (book) => {
    if (book.is_free) {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Descarga gratuita</Badge>;
    }
    if (book.stock === 0) {
      return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Sin stock</Badge>;
    }
    if (book.stock <= 3) {
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">¡Últimas {book.stock}!</Badge>;
    }
    return <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">{book.stock} disponibles</Badge>;
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-violet-50 to-white p-8 flex items-center justify-center" style={{ minHeight: '300px' }}>
        <div className="text-center">
          <BookOpen className="h-12 w-12 text-violet-600 animate-pulse mx-auto mb-4" />
          <p className="text-violet-700 font-medium">Cargando catálogo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-violet-50 to-white" style={{ fontFamily: "'Inter', system-ui, sans-serif", overflow: 'visible', minHeight: 'auto' }}>
      <Toaster position="top-center" richColors />

      {/* Search + Filter */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
        {/* Buscador */}
        <div className="bg-white rounded-full shadow-md flex items-center px-4 py-2 mb-4">
          <Search className="h-5 w-5 text-violet-400 mr-3 flex-shrink-0" />
          <Input
            type="text"
            placeholder="Buscar por título o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-0 focus-visible:ring-0 text-sm placeholder:text-gray-400"
            data-testid="libritos-search-input"
          />
        </div>
        
        {/* Filtro de categoría */}
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => setCategoryFilter("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              categoryFilter === "all"
                ? "bg-violet-600 text-white shadow-md"
                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
            data-testid="filter-all"
          >
            Todos
          </button>
          <button
            onClick={() => setCategoryFilter("sale")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              categoryFilter === "sale"
                ? "bg-violet-600 text-white shadow-md"
                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
            data-testid="filter-sale"
          >
            <ShoppingCart className="h-4 w-4" />
            A la venta
          </button>
          <button
            onClick={() => setCategoryFilter("free")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              categoryFilter === "free"
                ? "bg-blue-600 text-white shadow-md"
                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
            data-testid="filter-free"
          >
            <Download className="h-4 w-4" />
            Descarga gratuita
          </button>
        </div>
      </div>

      {/* Book Grid - Sin altura fija para adaptarse al contenido */}
      <main className="max-w-4xl mx-auto px-4 md:px-6 pb-8">
        {categoryFilteredBooks.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-violet-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-violet-900 mb-2">No se encontraron publicaciones</h3>
            <p className="text-gray-500 text-sm">Probá con otro término de búsqueda o categoría</p>
          </div>
        ) : (
          <div className="space-y-10">
            {/* Sección: Libros a la Venta */}
            {booksForSale.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <ShoppingCart className="h-5 w-5 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Libros a la Venta</h2>
                    <p className="text-sm text-gray-500">Reservá online y retirá en la sede</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {booksForSale.map((book) => (
                    <div
                      key={book.id}
                      className="bg-white rounded-xl border border-violet-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                      data-testid={`libritos-book-card-${book.id}`}
                    >
                      {/* Book Image */}
                      <div className="aspect-[3/4] bg-gradient-to-br from-violet-50 to-purple-50 relative overflow-hidden">
                        {book.image_url ? (
                          <img
                            src={book.image_url}
                            alt={book.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="h-16 w-16 text-violet-200" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2">
                          {getStockBadge(book)}
                        </div>
                      </div>

                      {/* Book Info */}
                      <div className="p-4">
                        <h3 className="font-semibold text-base text-gray-900 mb-1 line-clamp-2">
                          {book.title}
                        </h3>
                        {book.description && (
                          <p className="text-gray-500 text-xs mb-3 line-clamp-2">{book.description}</p>
                        )}
                        
                        {/* Precio */}
                        <div className="mb-3">
                          <span className="text-xl font-bold text-violet-700">
                            ${book.price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                          </span>
                        </div>

                        {/* Botones */}
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => openReservationModal(book)}
                            disabled={book.stock === 0}
                            className="w-full bg-violet-600 hover:bg-violet-700 text-white text-sm disabled:bg-gray-300"
                            data-testid={`libritos-reserve-btn-${book.id}`}
                          >
                            <ShoppingCart className="h-4 w-4 mr-2" />
                            Reservar
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setPaymentInfoModal(true)}
                            className="w-full text-sm border-violet-200 text-violet-700 hover:bg-violet-50"
                            data-testid={`libritos-info-btn-${book.id}`}
                          >
                            <Info className="h-4 w-4 mr-2" />
                            Cómo retiro y pago
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Sección: Descargas Gratuitas */}
            {freeBooks.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Download className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Descargas Gratuitas</h2>
                    <p className="text-sm text-gray-500">Material de acceso libre en formato PDF</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {freeBooks.map((book) => (
                    <div
                      key={book.id}
                      className="bg-white rounded-xl border border-blue-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                      data-testid={`libritos-book-card-${book.id}`}
                    >
                      {/* Book Image */}
                      <div className="aspect-[3/4] bg-gradient-to-br from-blue-50 to-indigo-50 relative overflow-hidden">
                        {book.image_url ? (
                          <img
                            src={book.image_url}
                            alt={book.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="h-16 w-16 text-blue-200" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Descarga gratuita</Badge>
                        </div>
                      </div>

                      {/* Book Info */}
                      <div className="p-4">
                        <h3 className="font-semibold text-base text-gray-900 mb-1 line-clamp-2">
                          {book.title}
                        </h3>
                        {book.description && (
                          <p className="text-gray-500 text-xs mb-3 line-clamp-2">{book.description}</p>
                        )}
                        
                        {/* Gratis */}
                        <div className="mb-3">
                          <span className="text-lg font-bold text-blue-600">Gratis</span>
                        </div>

                        {/* Botón de descarga */}
                        <Button
                          onClick={() => window.open(book.download_url, '_blank')}
                          disabled={!book.download_url}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm"
                          data-testid={`libritos-download-btn-${book.id}`}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Descargar PDF
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {/* Payment Info Modal */}
      <IframeModal 
        open={paymentInfoModal} 
        onClose={() => setPaymentInfoModal(false)}
        title={
          <span className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            {PAYMENT_INFO.title}
          </span>
        }
      >
        <div className="space-y-4">
          <div className="bg-violet-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-line">
            {PAYMENT_INFO.content.split('**').map((part, i) => 
              i % 2 === 1 ? <strong key={i}>{part}</strong> : part
            )}
          </div>
          <Button
            onClick={() => setPaymentInfoModal(false)}
            className="w-full bg-violet-600 hover:bg-violet-700"
          >
            Entendido
          </Button>
        </div>
      </IframeModal>

      {/* Reservation Modal */}
      <IframeModal 
        open={reservationModal} 
        onClose={() => setReservationModal(false)}
        title="Reservar Publicación"
      >
        <p className="text-sm text-gray-500 mb-4">
          Completá tus datos para reservar <strong>{selectedBook?.title}</strong>
        </p>
        <form onSubmit={handleReservation} className="space-y-4">
          <div className="bg-violet-50 rounded-lg p-3 text-sm">
            <p className="text-violet-700">
              <strong>Precio:</strong> ${selectedBook?.price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
            </p>
          </div>

          <div>
            <Label htmlFor="customer_name">Nombre y Apellido *</Label>
            <Input
              id="customer_name"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              required
              className="mt-1"
              data-testid="libritos-input-customer-name"
            />
          </div>

          <div>
            <Label htmlFor="customer_email">Email *</Label>
            <Input
              id="customer_email"
              type="email"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              required
              className="mt-1"
              data-testid="libritos-input-customer-email"
            />
          </div>

          <div>
            <Label htmlFor="customer_phone">Teléfono / WhatsApp *</Label>
            <Input
              id="customer_phone"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              required
              className="mt-1"
              data-testid="libritos-input-customer-phone"
            />
          </div>

          {/* Método de Pago */}
          <div>
            <Label className="mb-2 block">¿Cómo vas a pagar? *</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, payment_method: "efectivo" })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.payment_method === "efectivo"
                    ? "border-violet-500 bg-violet-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid="payment-method-efectivo"
              >
                <Banknote className={`h-5 w-5 mb-1 ${formData.payment_method === "efectivo" ? "text-violet-600" : "text-gray-400"}`} />
                <p className="font-medium text-sm">Efectivo</p>
                <p className="text-xs text-gray-500">Pago en sede</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, payment_method: "transferencia" })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.payment_method === "transferencia"
                    ? "border-violet-500 bg-violet-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid="payment-method-transferencia"
              >
                <CreditCard className={`h-5 w-5 mb-1 ${formData.payment_method === "transferencia" ? "text-violet-600" : "text-gray-400"}`} />
                <p className="font-medium text-sm">Transferencia</p>
                <p className="text-xs text-gray-500">Pago anticipado</p>
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Tu reserva vence en 72 hs. Recibirás un email con las instrucciones.
          </p>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setReservationModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-violet-600 hover:bg-violet-700"
              data-testid="libritos-confirm-reservation-btn"
            >
              {submitting ? "Procesando..." : "Confirmar"}
            </Button>
          </div>
        </form>
      </IframeModal>

      {/* Success Modal */}
      <IframeModal 
        open={successModal} 
        onClose={() => setSuccessModal(false)}
        title="¡Reserva Confirmada!"
      >
        <div className="text-center">
          <div className="mx-auto w-14 h-14 bg-violet-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-violet-600" />
          </div>
          
          <div className="space-y-4">
            <p className="text-sm">Tu reserva fue registrada. Tenés 72 hs para retirarla.</p>
            
            {reservationData && (
              <div className="bg-violet-50 rounded-lg p-4 text-left text-sm">
                <p className="text-violet-800 mb-1">
                  <strong>ID:</strong> {reservationData.id}
                </p>
                <p className="text-violet-800 mb-1">
                  <strong>Libro:</strong> {reservationData.book_title}
                </p>
                <p className="text-violet-800 mb-1">
                  <strong>Precio:</strong> ${reservationData.book_price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                </p>
                <p className="text-violet-800">
                  <strong>Pago:</strong> {reservationData.payment_method === 'transferencia' ? 'Transferencia' : 'Efectivo en sede'}
                </p>
              </div>
            )}

            {reservationData?.payment_method === 'transferencia' ? (
              <div className="bg-amber-50 rounded-lg p-3 text-left text-sm">
                <p className="text-amber-800 font-medium mb-2">📤 Siguiente paso:</p>
                <p className="text-amber-700 mb-3">
                  Hacé la transferencia y subí el comprobante desde "Mis Reservas" para que podamos confirmar tu pago.
                </p>
                <Button
                  onClick={() => window.open(`/libritos/mis-reservas?email=${reservationData?.customer_email}`, '_blank')}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-sm"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ir a Mis Reservas
                </Button>
              </div>
            ) : (
              <div className="bg-green-50 rounded-lg p-3 text-left text-sm">
                <p className="text-green-800 font-medium mb-2">💵 Pago en efectivo</p>
                <p className="text-green-700">
                  Abonás cuando retires el libro en la sede. Te enviamos un email con los detalles.
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500">
              Te enviamos un email con toda la información a {reservationData?.customer_email}
            </p>

            <Button
              onClick={() => setSuccessModal(false)}
              variant="outline"
              className="w-full"
              data-testid="libritos-close-success-modal"
            >
              Cerrar
            </Button>
          </div>
        </div>
      </IframeModal>
    </div>
  );
}
